import { useCallback, useState } from 'react'
import { getUrl, postJson } from '../../providers/data'
import {
  AnalyzeRequest,
  AnalyzeResponse,
  BuildRequest,
  BuildResponse,
  GeneratorMessage,
} from '../../types/apiRequests'

export type GeneratorStep =
  | 'describing'
  | 'analyzing'
  | 'clarifying'
  | 'summary'
  | 'building'
  | 'done'
  | 'error'

interface GeneratorState {
  step: GeneratorStep
  messages: GeneratorMessage[]
  questions: string[]
  summary: string
  workflowId: string
  errorMessage: string
  requestId: string
}

const INITIAL: GeneratorState = {
  step: 'describing',
  messages: [],
  questions: [],
  summary: '',
  workflowId: '',
  errorMessage: '',
  requestId: '',
}

export const useWorkflowGenerator = () => {
  const [state, setState] = useState<GeneratorState>(INITIAL)

  const reset = useCallback(() => setState(INITIAL), [])

  const setRequestId = useCallback((requestId: string) => {
    setState((prev) => (prev.requestId === requestId ? prev : { ...prev, requestId }))
  }, [])

  const goToDescribe = useCallback(() => {
    // Reset conversation for a clean re-analyze; preserve requestId so the build still links to the originating request.
    setState((prev) => ({
      ...prev,
      step: 'describing',
      errorMessage: '',
      messages: [],
      summary: '',
      questions: [],
    }))
  }, [])

  const startFromApprovedRequest = useCallback(
    (prompt: string, summary: string, requestId: string) => {
      // Seed on the summary step — re-running analyze could produce a different summary than the approved one.
      setState({
        ...INITIAL,
        messages: prompt ? [{ role: 'user', content: prompt }] : [],
        summary,
        step: 'summary',
        requestId,
      })
    },
    [],
  )

  const submitDescription = useCallback(async (text: string) => {
    let snapshot: GeneratorState | null = null
    setState((prev) => {
      const nextMessages: GeneratorMessage[] = [
        ...prev.messages,
        { role: 'user', content: text },
      ]
      snapshot = {
        ...prev,
        messages: nextMessages,
        step: 'analyzing',
        errorMessage: '',
      }
      return snapshot
    })
    if (!snapshot) return
    const { messages } = snapshot
    try {
      const body: AnalyzeRequest = { messages }
      const result = await postJson<AnalyzeResponse>(
        `${getUrl()}/workflow_generation/analyze`,
        body,
      )
      if (result.status === 'needs_clarification') {
        const questions = result.questions ?? []
        const assistantContent = questions.length
          ? questions.map((q) => `- ${q}`).join('\n')
          : ''
        setState((prev) => ({
          ...prev,
          questions,
          messages: assistantContent
            ? [...messages, { role: 'user', content: assistantContent }]
            : messages,
          step: 'clarifying',
        }))
      } else {
        setState((prev) => ({
          ...prev,
          summary: result.summary ?? '',
          step: 'summary',
        }))
      }
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : String(err)
      setState((prev) => ({ ...prev, step: 'error', errorMessage }))
    }
  }, [])

  const build = useCallback(async () => {
    let snapshot: GeneratorState | null = null
    setState((prev) => {
      snapshot = { ...prev, step: 'building', errorMessage: '' }
      return snapshot
    })
    if (!snapshot) return
    const { messages, summary, requestId } = snapshot
    try {
      // eslint-disable-next-line camelcase
      const body: BuildRequest = { messages, summary, request_id: requestId || null }
      const result = await postJson<BuildResponse>(
        `${getUrl()}/workflow_generation/build`,
        body,
      )
      setState((prev) => ({
        ...prev,
        step: 'done',
        workflowId: result.workflow_id,
      }))
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : String(err)
      setState((prev) => ({ ...prev, step: 'error', errorMessage }))
    }
  }, [])

  return {
    ...state,
    submitDescription,
    build,
    reset,
    goToDescribe,
    setRequestId,
    startFromApprovedRequest,
  }
}
